import React from 'react';
import styled from 'styled-components';

const RulesContainer = styled.div`
  padding: 4rem 8rem;
  max-width: 1800px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 1200px) {
    padding: 4rem;
    grid-template-columns: 1fr;
  }
  
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const PageTitleWrapper = styled.div`
  grid-column: 1 / -1;
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  position: relative;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 30px rgba(255, 51, 51, 0.3);
  }
`;

const PageSubtitle = styled.p`
  color: #aaa;
  text-align: center;
  max-width: 700px;
  margin: 0 auto 3rem;
  font-family: 'Poppins', sans-serif;
  line-height: 1.6;
  
  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: var(--primary-color);
    margin: 20px auto 0;
  }
`;

const RuleSection = styled.div`
  background: rgba(10, 10, 10, 0.8);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: var(--primary-color);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
  }
`;

const SectionTitle = styled.h2`
  color: #fff;
  margin-bottom: 1.5rem;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  background: linear-gradient(45deg, var(--primary-color), rgba(255, 51, 51, 0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, var(--primary-color), transparent);
  }
`;

const RuleList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  gap: 1rem;
`;

const RuleItem = styled.li`
  color: #fff;
  padding: 1rem 1.5rem;
  position: relative;
  font-family: 'Poppins', sans-serif;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: var(--primary-color);
    transform: translateX(5px);
  }
  
  &:before {
    content: "•";
    color: var(--primary-color);
    position: absolute;
    left: 0.5rem;
    font-weight: bold;
  }
`;

const Rules = () => {
  return (
    <RulesContainer>
      <PageTitleWrapper>
        <PageTitle>SERVER RULES</PageTitle>
        <PageSubtitle>
          Please read and follow our server rules carefully. These guidelines help maintain a friendly and enjoyable environment for all players.
        </PageSubtitle>
      </PageTitleWrapper>
      
      <RuleSection>
        <SectionTitle>General Rules</SectionTitle>
        <RuleList>
          <RuleItem>Be respectful to all players and staff members.</RuleItem>
          <RuleItem>No harassment, bullying, or discrimination of any kind.</RuleItem>
          <RuleItem>No spamming or flooding the chat with messages.</RuleItem>
          <RuleItem>No excessive use of caps or special characters in chat.</RuleItem>
          <RuleItem>No advertising other servers or websites.</RuleItem>
          <RuleItem>English only in public chat channels.</RuleItem>
          <RuleItem>Follow staff instructions at all times.</RuleItem>
        </RuleList>
      </RuleSection>
      
      <RuleSection>
        <SectionTitle>Gameplay Rules</SectionTitle>
        <RuleList>
          <RuleItem>No hacking, cheating, or using mods that give an unfair advantage.</RuleItem>
          <RuleItem>No exploiting bugs or glitches – report them to staff instead.</RuleItem>
          <RuleItem>No griefing or destroying other players' builds without permission.</RuleItem>
          <RuleItem>No stealing from other players.</RuleItem>
          <RuleItem>No intentional lagging or crashing of the server.</RuleItem>
          <RuleItem>PvP is only allowed in designated areas.</RuleItem>
          <RuleItem>Respect claim boundaries and protected areas.</RuleItem>
        </RuleList>
      </RuleSection>
      
      <RuleSection>
        <SectionTitle>Chat Rules</SectionTitle>
        <RuleList>
          <RuleItem>No swearing or inappropriate language.</RuleItem>
          <RuleItem>No discussing political or religious topics.</RuleItem>
          <RuleItem>No sharing personal information of yourself or others.</RuleItem>
          <RuleItem>No impersonating staff or other players.</RuleItem>
          <RuleItem>No excessive arguing in public chat.</RuleItem>
          <RuleItem>Use appropriate channels for different topics.</RuleItem>
          <RuleItem>Report rule breakers to staff instead of confronting them in chat.</RuleItem>
        </RuleList>
      </RuleSection>
      
      <RuleSection>
        <SectionTitle>Consequences</SectionTitle>
        <RuleList>
          <RuleItem>First offense: Warning</RuleItem>
          <RuleItem>Second offense: Temporary mute or ban (duration depends on severity)</RuleItem>
          <RuleItem>Third offense: Longer temporary ban</RuleItem>
          <RuleItem>Severe or repeated offenses: Permanent ban</RuleItem>
          <RuleItem>Staff have final say in all moderation decisions.</RuleItem>
          <RuleItem>Ban appeals can be submitted on our Discord server.</RuleItem>
          <RuleItem>Rules are subject to change at any time—stay updated.</RuleItem>
        </RuleList>
      </RuleSection>
    </RulesContainer>
  );
};

export default Rules;
